import React, { useEffect, useRef, useState } from 'react'
import theme from '../../config/theme'
import { IoTrashOutline, IoClose } from 'react-icons/io5'
import { userApi } from '../../utils/apiClient'
import { useAuth } from '../../contexts/authContext'
import { UserManagementDto } from '../../dtos/userManagement.dto'

export interface DeleteDialogProps {
  userId?: number
  userEmail?: string
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({
  open,
  userId,
  userEmail,
  onClose,
  onSuccess,
}) => {
  const { user: currentUser } = useAuth()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Check if user is trying to delete themselves
  const isSelfDelete = currentUser?.email === userEmail

  // Close dialog on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  // Close dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose])

  const handleConfirm = async () => {
    if (!userId) {
      return
    }

    // Prevent self-deletion
    if (isSelfDelete) {
      setError('You cannot delete your own account')
      return
    }

    setIsDeleting(true)
    setError(null)

    try {
      await userApi.deleteUser(userId)
      
      // Success - close dialog and refresh user list
      onClose()
      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user'
      setError(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  // Reset error when dialog opens/closes
  useEffect(() => {
    if (open) {
      setError(null)
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        ref={dialogRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0" style={{ borderColor: theme.color.light300 }}>
          <div className="flex items-center gap-4">
            {/* Icon with background circle */}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: theme.color.error + '20' }}
            >
              {React.createElement(IoTrashOutline as any, {
                className: "w-5 h-5",
                style: { color: theme.color.error }
              })}
            </div>
            <h2 id="delete-dialog-title" className="text-xl font-medium" style={{ color: theme.color.gray500 }}>
              Delete User Account ?
            </h2>
          </div>
          <button
            type="button"
            className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
            onClick={onClose}
            aria-label="Close dialog"
            style={{ color: theme.color.gray200 }}
          >
            {React.createElement(IoClose as any, { className: "h-6 w-6" })}
          </button>
        </div>

        {/* Message */}
        <div className="px-6 py-4 ml-14 font-light" style={{ color: theme.color.gray500 }}>
          Are you sure you want to delete this user? This action cannot be undone.
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 py-3 mx-6 mb-2 rounded-lg text-sm" style={{
            backgroundColor: `${theme.color.error}20`,
            borderColor: `${theme.color.error}50`,
            color: theme.color.error,
            border: '1px solid'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 px-6 pt-4 pb-6" style={{ borderColor: theme.color.light300 }}>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg border text-sm font-medium transition-all"
            style={{
              borderColor: theme.color.light500,
              backgroundColor: theme.color.light100,
              color: theme.color.gray500
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isDeleting || isSelfDelete}
            className="px-6 py-2 rounded-lg border-0 text-sm font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: theme.color.error,
              color: theme.color.light100
            }}
            onMouseEnter={(e) => {
              if (!isDeleting && !isSelfDelete) {
                e.currentTarget.style.opacity = '0.9'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            {isDeleting ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </span>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteDialog

